import { CheckIcon } from '@/icons';
import { type TableProps } from 'antd';
import dayjs from 'dayjs';
import {
  useGetSeatUsageQuery,
  useGetSubscriptionByAdminIdQuery,
} from '../../../../app/features/onboarding/onboarding.api';
import type { SubscriptionData } from '../../../../app/features/onboarding/onboarding.type';
import CustomTable from '../../../../components/CustomTable';
import PageTitle from '../../../../components/PageTitle';
import useAuth from '../../../../hooks/useAuth';

export default function Subscriptions() {
  // userId is the selected organization's employee for multi-organization
  // logins (the login user owns no subscription); same as before otherwise.
  const { userId } = useAuth();

  // Fetch subscription data
  const { data: subscriptionData, isLoading } =
    useGetSubscriptionByAdminIdQuery(String(userId), {
      skip: !userId,
    });

  const subscription = subscriptionData?.data?.[0]; // Get first subscription

  // Older backends have no seat-usage endpoint: fall back to the plan's max_user.
  const { data: seatUsageData } = useGetSeatUsageQuery();
  const seatUsage = seatUsageData?.data;
  const totalSeats =
    seatUsage?.plan_max_seat ?? subscription?.plan_details?.max_user ?? 0;

  // Extract features from plan modules
  const features =
    subscription?.plan_details?.planModules?.map(
      (module: any) => `${module.module.name} (${module.module.description})`,
    ) || [];

  const midPoint = Math.ceil(features.length / 2);

  const leftFeatures = features.slice(0, midPoint);
  const rightFeatures = features.slice(midPoint);

  const columns: TableProps<SubscriptionData>['columns'] = [
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : 'n/a'),
    },
    {
      title: 'Plan Name',
      dataIndex: 'plan_name',
      render: (val) => val ?? 'n/a',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (val) => (val ? `৳${val}` : 'n/a'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (val) => (val ? 'Active' : 'Inactive'),
    },
    // {
    //   title: 'Actions',
    //   dataIndex: 'id',
    //   align: 'center',
    //   render: (id: string) => (
    //     <Space>
    //       <Link to={`/subscription/${id}`}>
    //         <Button className="action-btn" size="small" icon={<ViewIcon />} />
    //       </Link>
    //       <Button className="action-btn" size="small" icon={<DeleteIcon />} />
    //     </Space>
    //   ),
    // },
  ];
  return (
    <>
      <div>
        <PageTitle title="Subscription" />
        <div className="dark:bg-theme-slate-800 dark:border-theme-slate-800 p-6 rounded-xl text-white w-full mb-5 mt-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">
              {subscription?.plan_name || 'Plan'}
            </h2>
            <p className="text-sm text-gray-300">
              Renewal Date:{' '}
              <span className="text-white font-medium">
                {subscription?.renewal_date
                  ? dayjs(subscription.renewal_date).format('DD/MM/YYYY')
                  : 'N/A'}
              </span>
            </p>
          </div>

          {/* Divider */}
          <div className="border border-gray-700 my-4"></div>

          {/* Features */}
          {/* Features */}
          <div className="grid grid-cols-2 gap-x-6 mt-6">
            {/* LEFT COLUMN */}
            <ul className="space-y-2 text-sm">
              {leftFeatures.map((f: any, i: any) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckIcon size={18} className="text-[#00C6AE]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* RIGHT COLUMN */}
            <ul className="space-y-2 text-sm">
              {rightFeatures.map((f: any, i: any) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckIcon size={18} className="text-[#00C6AE]" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Divider */}
          <div className="border border-gray-700 my-4"></div>

          {/* Bottom Section */}
          <div className="grid grid-cols-4 gap-4 mt-8 ">
            <div>
              <p className="text-sm text-white">Pricing</p>
              <h3 className="text-xl font-bold">
                ৳{subscription?.price || '0'} /{' '}
                {subscription?.is_monthly ? 'month' : 'year'}
              </h3>
              {/* <Button
                type="link"
                className="!p-0 !text-theme-primary underline underline-offset-2"
              >
                Upgrade Plan
              </Button> */}
            </div>

            <div>
              <p className="text-sm text-white">Total Seats</p>
              <h3 className="text-xl font-bold">{totalSeats}</h3>
              {/* <Button
                type="link"
                className="!p-0 !text-theme-primary underline underline-offset-2"
              >
                Upgrade
              </Button> */}
            </div>

            <div>
              <p className="text-sm text-white">Used Seats</p>
              <h3 className="text-xl font-bold">
                {seatUsage?.used_seats ?? 'N/A'}
              </h3>
            </div>

            <div>
              <p className="text-sm text-white">Available Seats</p>
              <h3 className="text-xl font-bold">
                {seatUsage?.free_seats ?? 'N/A'}
              </h3>
            </div>

          </div>
        </div>{' '}
        <CustomTable
          customTitle="Subscriptions"
          tableRootClassName="border-0"
          columns={columns}
          dataSource={subscriptionData?.data || []}
          hideRowSelect
          total={subscriptionData?.paginationData?.total || 0}
          limit={subscriptionData?.paginationData?.limit || 10}
          loading={isLoading}
        />
      </div>
    </>
  );
}
